import React, { Suspense } from "react";
import { Routes, Route, useParams, Navigate } from "react-router-dom";
import { routes } from "./routesConfig";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { courseData } from "../components/teachersdashboard/data";
import apiService from "../services/api.services";

// Loading component
const Loading = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// ✅ UPDATED: Protected Route Component with role-based redirects
interface ProtectedRouteProps {
  children: React.ReactElement;
  requireAuth?: boolean;
  redirectTo?: string;
  allowedRoles?: string[];
  redirectAuthenticatedTo?: string; // ✅ NEW: Redirect authenticated users (for login/signup pages)
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true,
  redirectTo,
  allowedRoles,
  redirectAuthenticatedTo // ✅ NEW
}) => {
  const authStatus = apiService.authService.getAuthStatus();
  const { isAuthenticated, dashboardRoute, user } = authStatus;
  // If this is a guest-only page (like login/signup) and user is authenticated
  // Redirect them to their dashboard
  if (redirectAuthenticatedTo && isAuthenticated) {
    // If redirectAuthenticatedTo is "dashboard", use the role-based dashboard route
    const redirectPath = redirectAuthenticatedTo === "dashboard" 
      ? dashboardRoute 
      : redirectAuthenticatedTo;
    return <Navigate to={redirectPath} replace />;
  }

  // If route is public (requireAuth=false), allow access
  // Don't redirect authenticated users away from public pages
  if (!requireAuth) {
    return children;
  }

  // If route requires auth and user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo || "/login"} replace />;
  }

  // ✅ Check if user has required role (only for protected routes)
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user?.role?.toLowerCase();
    const hasAllowedRole = allowedRoles.some(
      role => role.toLowerCase() === userRole
    );
    
    if (!hasAllowedRole) {
      // Redirect to their appropriate dashboard if they don't have access
      return <Navigate to={dashboardRoute} replace />;
    }
  }

  return children;
};

// Special component to handle dynamic registration forms
function RegisterForm() {
  const { role } = useParams();
  const TeacherRegisterForm = React.lazy(() =>
    import("../components/Register/tutors/TeacherRegisterForm")
  );
  const OrganizationRegistrationForm = React.lazy(() =>
    import("../components/Register/organization/OrganisationRegistration")
  );
  const StudentRegisterForm = React.lazy(() =>
    import("../components/Register/student/StudentRegisterForm")
  );

  const renderForm = () => {
    switch (role) {
      case "tutor":
        return <TeacherRegisterForm paddingTop={16} />;
      case "organization":
        return <OrganizationRegistrationForm />;
      case "student":
        return <StudentRegisterForm paddingTop={20} />;
      default:
        return <StudentRegisterForm paddingTop={24} />;
    }
  };

  return <Suspense fallback={<Loading />}>{renderForm()}</Suspense>;
}

// Stripe setup
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

// Wrapper component for Stripe Elements
const StripeWrapper = ({ children }: { children: React.ReactNode }) => {
  return <Elements stripe={stripePromise}>{children}</Elements>;
};

const Router = () => {
  // Helper function to render routes recursively
  const renderRoutes = (routeConfig: any[]) => {
    return routeConfig.map((route) => {
      const RouteElement = route.element;

      // Handle nested routes
      if (route.children) {
        const parentElement = (
          <Suspense fallback={<Loading />}>
            <RouteElement />
          </Suspense>
        );

        // ✅ UPDATED: Wrap parent with ProtectedRoute with role support
        const wrappedParent = route.protected !== undefined ? (
          <ProtectedRoute 
            requireAuth={route.protected}
            allowedRoles={route.allowedRoles}
            redirectAuthenticatedTo={route.redirectAuthenticatedTo} // ✅ NEW
          >
            {parentElement}
          </ProtectedRoute>
        ) : parentElement;

        return (
          <Route
            key={route.path}
            path={route.path}
            element={wrappedParent}
          >
            {route.children.map((childRoute: any) => {
              const ChildElement = childRoute.element;

              if (childRoute.index) {
                // Handle special redirect index routes
                if (typeof childRoute.element === "string" && childRoute.element.startsWith("REDIRECT_")) {
                  const REDIRECT_MAP: Record<string, string> = {
                    REDIRECT_ORG_DASHBOARD: "/organization/dashboard",
                    REDIRECT_ADMIN_DASHBOARD: "/admin/dashboard",
                  };
                  const redirectTo = REDIRECT_MAP[childRoute.element] ?? "/";
                  return (
                    <Route
                      key="index"
                      index
                      element={<Navigate to={redirectTo} replace />}
                    />
                  );
                }

                const indexElement = (
                  <Suspense fallback={<Loading />}>
                    <ChildElement />
                  </Suspense>
                );

                return (
                  <Route
                    key="index"
                    index
                    element={
                      childRoute.protected !== undefined ? (
                        <ProtectedRoute
                          requireAuth={childRoute.protected}
                          allowedRoles={childRoute.allowedRoles}
                          redirectAuthenticatedTo={childRoute.redirectAuthenticatedTo} // ✅ NEW
                        >
                          {indexElement}
                        </ProtectedRoute>
                      ) : indexElement
                    }
                  />
                );
              }

              if (childRoute.element === "REGISTER_FORM") {
                const registerElement = (
                  <Suspense fallback={<Loading />}>
                    <RegisterForm />
                  </Suspense>
                );

                return (
                  <Route
                    key={childRoute.path}
                    path={childRoute.path}
                    element={
                      childRoute.protected !== undefined ? (
                        <ProtectedRoute 
                          requireAuth={childRoute.protected}
                          allowedRoles={childRoute.allowedRoles}
                          redirectAuthenticatedTo={childRoute.redirectAuthenticatedTo} // ✅ NEW
                        >
                          {registerElement}
                        </ProtectedRoute>
                      ) : registerElement
                    }
                  />
                );
              }

              // Handle Stripe Elements wrapper for child routes
              if (childRoute.wrapWithElements) {
                const stripeElement = (
                  <Suspense fallback={<Loading />}>
                    <StripeWrapper>
                      <ChildElement />
                    </StripeWrapper>
                  </Suspense>
                );

                return (
                  <Route
                    key={childRoute.path}
                    path={childRoute.path}
                    element={
                      childRoute.protected !== undefined ? (
                        <ProtectedRoute 
                          requireAuth={childRoute.protected}
                          allowedRoles={childRoute.allowedRoles}
                          redirectAuthenticatedTo={childRoute.redirectAuthenticatedTo} // ✅ NEW
                        >
                          {stripeElement}
                        </ProtectedRoute>
                      ) : stripeElement
                    }
                  />
                );
              }

              const childElement = (
                <Suspense fallback={<Loading />}>
                  <ChildElement />
                </Suspense>
              );

              return (
                <Route
                  key={childRoute.path}
                  path={childRoute.path}
                  element={
                    childRoute.protected !== undefined ? (
                      <ProtectedRoute 
                        requireAuth={childRoute.protected}
                        allowedRoles={childRoute.allowedRoles}
                        redirectAuthenticatedTo={childRoute.redirectAuthenticatedTo} // ✅ NEW
                      >
                        {childElement}
                      </ProtectedRoute>
                    ) : childElement
                  }
                />
              );
            })}
          </Route>
        );
      }

      // Handle Stripe Elements wrapper for top-level routes
      if (route.wrapWithElements) {
        const stripeElement = (
          <Suspense fallback={<Loading />}>
            <StripeWrapper>
              <RouteElement />
            </StripeWrapper>
          </Suspense>
        );

        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.protected !== undefined ? (
                <ProtectedRoute 
                  requireAuth={route.protected}
                  allowedRoles={route.allowedRoles}
                  redirectAuthenticatedTo={route.redirectAuthenticatedTo} // ✅ NEW
                >
                  {stripeElement}
                </ProtectedRoute>
              ) : stripeElement
            }
          />
        );
      }

      // Handle components that need props
      if (route.path === "course-details") {
        const courseElement = (
          <Suspense fallback={<Loading />}>
            <RouteElement course={courseData} />
          </Suspense>
        );

        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.protected !== undefined ? (
                <ProtectedRoute 
                  requireAuth={route.protected}
                  allowedRoles={route.allowedRoles}
                  redirectAuthenticatedTo={route.redirectAuthenticatedTo} // ✅ NEW
                >
                  {courseElement}
                </ProtectedRoute>
              ) : courseElement
            }
          />
        );
      }

      // Handle standard routes
      const standardElement = (
        <Suspense fallback={<Loading />}>
          <RouteElement />
        </Suspense>
      );

      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            route.protected !== undefined ? (
              <ProtectedRoute 
                requireAuth={route.protected}
                allowedRoles={route.allowedRoles}
                redirectAuthenticatedTo={route.redirectAuthenticatedTo} // ✅ NEW
              >
                {standardElement}
              </ProtectedRoute>
            ) : standardElement
          }
        />
      );
    });
  };

  return <Routes>{renderRoutes(routes)}</Routes>;
};

export default Router;